import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PUBLIC_KEY_V2 } from '../../../Config/app/reCaptcha';

type Props = {
  onChange: (token: string) => void;
  onFocusHandled: () => void;
  isInvalid: boolean;
  shouldFocus: boolean;
  value?: string;
};

export const ReCaptchaCheckbox = ({
  onChange,
  onFocusHandled,
  isInvalid,
  shouldFocus,
  value,
}: Props) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const id = useRef<number | null>(null);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const grecaptcha = window.grecaptcha as typeof window.grecaptcha | undefined;
    if (!executeRecaptcha || !grecaptcha?.render) return;

    if (id.current === null) {
      try {
        id.current = grecaptcha.render('recaptcha-checkbox', {
          sitekey: PUBLIC_KEY_V2,
          callback: (token: string) => {
            onChange(token);
          },
        });
      } catch (error) {
        console.error('Failed to render reCAPTCHA fallback:', error);
        return;
      }
    }

    const widgetId = id.current;
    return () => grecaptcha.reset(widgetId);
  }, [executeRecaptcha]);

  // Clearing a consumed token must also reset the single-use checkbox widget.
  useEffect(() => {
    const grecaptcha = window.grecaptcha as typeof window.grecaptcha | undefined;
    if (value === '' && id.current !== null && grecaptcha?.reset) {
      grecaptcha.reset(id.current);
    }
  }, [executeRecaptcha, value]);

  useEffect(() => {
    if (shouldFocus && id.current !== null) {
      wrapper.current?.focus();
      onFocusHandled();
    }
  }, [executeRecaptcha, onFocusHandled, shouldFocus]);

  return (
    <div
      ref={wrapper}
      className="flex justify-center"
      role="group"
      aria-label="reCAPTCHA"
      tabIndex={-1}
    >
      <div
        id="recaptcha-checkbox"
        className={classNames({ 'bg-red-500': isInvalid })}
        style={{ borderRadius: '2px', paddingTop: '2px', paddingLeft: '2px' }}
      />
    </div>
  );
};
